import { useMenu } from "@/hooks/useMenu";
import useScrollFade from "@/hooks/useScrollFade";
import { useVenue } from "@/hooks/useVenue";
import {
  type IconDefinition,
  faChartBar,
  faChevronRight,
  faClipboardList,
  faCog,
  faGift,
  faMobile,
  faQuestionCircle,
  faSignOutAlt,
  faUndoAlt,
  faUserChef,
  faUsers,
} from "@/icons/solid-icons";
import type { Venue } from "@/types/api/contracts/venue";
import { FeatureFlag } from "@/types/auth/FeatureFlags";
import {
  type ISidebarLink,
  SidebarLinkType,
  SidebarSection,
} from "@/types/layout/navigation";
import { cn } from "@/utils/css";
import Icon from "@dinesapp/dines-ui/components/Icon";
import { onShowConfirmAlert } from "@dinesapp/dines-ui/utils/alerts";
import { signOut } from "next-auth/react";
import Router, { useRouter } from "next/router";
import { useEffect, useState } from "react";
import MainSideBarLinks from "./links";
import SideBarProfile from "./profile";
import SideBarTitle from "./title";

/**
 * Interface representing the props for the main sidebar.
 * @interface IMainSideBar
 * @property {Object} [organisation] — The organisation the sidebar belongs to.
 * @property {string} selected — The id of the currently selected nav link.
 * @property {Function} setCollapseSidebar — Toggles the collapsed sidebar state.
 * @property {boolean} collapseSidebar — Whether the sidebar is collapsed.
 * @property {Function} onToggleVenueModal — Opens or closes the venue switcher modal.
 */
interface IMainSideBar {
  organisation?: Venue;
  selected: string;
  setCollapseSidebar: (collapseSidebar: boolean) => void;
  collapseSidebar: boolean;
  onToggleVenueModal: () => void;
}

const MainSideBar: React.FC<IMainSideBar> = ({
  organisation,
  selected,
  setCollapseSidebar,
  collapseSidebar,
  onToggleVenueModal,
}) => {
  const router = useRouter();
  const { menuOpen, toggleMenu, setMenuOpen } = useMenu();
  const { selectedVenue, venues, isVenueSpecific, getFeatureFlags } =
    useVenue();

  const featureFlags = getFeatureFlags();

  const onSignOut = () => {
    onShowConfirmAlert({
      labels: {
        id: "logout-confirm-alert",
        title: "Are you sure you want to log out?",
        yesLabel: "Log out",
      },

      visibilityOptions: {
        showNoButton: false,
        showYesButton: true,
      },

      onYes: () => {
        signOut();
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <No dependencies needed>
  useEffect(() => {
    const handleRouteChange = () => {
      setMenuOpen(false);
    };

    Router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if (
      router.pathname.startsWith("/redeemables") ||
      router.pathname === "/inventory"
    ) {
      return setCollapseSidebar(true);
    }
    return setCollapseSidebar(false);
  }, [router.pathname, setCollapseSidebar]);

  const allLinks: ISidebarLink[] = [
    {
      name: "Linked Accounts",
      type: SidebarLinkType.Link,
      url: "/accounts",
      id: "accounts",
      external: false,
      icon: faUsers,
      isVisible: !featureFlags[FeatureFlag.HideLinkedAccounts],
      section: SidebarSection.Manage,
    },
    {
      name: "Global Menus",
      type: SidebarLinkType.Link,
      url: "/inventory",
      id: "inventory",
      external: false,
      icon: faClipboardList,
      isVisible: !featureFlags[FeatureFlag.HideGlobalMenus],
      section: SidebarSection.Manage,
    },
    {
      name: "Past Orders",
      type: SidebarLinkType.Link,
      url: "/past-orders",
      id: "past-orders",
      external: false,
      icon: faUndoAlt,
      isVisible: !featureFlags[FeatureFlag.HidePastOrders],
      section: SidebarSection.Operate,
    },
    {
      name: "Reports",
      type: SidebarLinkType.Link,
      url: "/reports",
      id: "reports",
      external: false,
      icon: faChartBar,
      isVisible: !featureFlags[FeatureFlag.HideReports],
      section: SidebarSection.Operate,
    },
    {
      name: "Devices",
      type: SidebarLinkType.Link,
      url: "/devices",
      id: "devices",
      external: false,
      icon: faMobile,
      isVisible: !featureFlags[FeatureFlag.HideDevices],
      section: SidebarSection.System,
    },
    {
      name: "Redeemables",
      type: SidebarLinkType.Link,
      url: "/redeemables/code-lookup",
      id: "redeemables",
      external: false,
      icon: faGift,
      isVisible: featureFlags[FeatureFlag.RedeemablesV2],
      section: SidebarSection.Manage,
    },
    {
      name: "Settings",
      type: SidebarLinkType.Link,
      url: "/settings",
      id: "settings",
      external: false,
      icon: faCog,
      isVisible: !featureFlags[FeatureFlag.HideSettings],
      section: SidebarSection.System,
    },
    {
      name: "Support",
      type: SidebarLinkType.Link,
      url: "https://dines.co.uk/chat",
      id: "support",
      external: true,
      icon: faQuestionCircle,
      isVisible: true,
      section: SidebarSection.System,
    },
    {
      name: "Log out",
      type: SidebarLinkType.Action,
      id: "logout",
      icon: faSignOutAlt,
      action: onSignOut,
      external: false,
      isVisible: true,
      section: SidebarSection.System,
    },
  ];

  const links = allLinks.filter((link) => link.isVisible);

  const { scrollRef, showTopFade, showBottomFade, onScroll } = useScrollFade([
    links.length,
    collapseSidebar,
  ]);

  return (
    <div
      id="main-sidebar"
      className="flex h-full flex-col overflow-hidden text-white"
    >
      <SideBarTitle
        collapseSidebar={collapseSidebar}
        setCollapseSidebar={setCollapseSidebar}
        menuOpen={menuOpen}
        toggleMenu={toggleMenu}
      />

      {isVenueSpecific && (
        <div className="shrink-0 px-4">
          <div
            className={cn(
              "mb-2 px-4 font-semibold text-gray-500 text-xs uppercase tracking-widest",
              collapseSidebar && "md:px-0 md:text-center",
            )}
          >
            VENUE
          </div>
          <button
            type="button"
            onClick={onToggleVenueModal}
            className={cn(
              "flex h-12 w-full items-center rounded-xl bg-white/5 px-4 ring-1 ring-white/10 transition-colors duration-150 hover:bg-white/10",
              collapseSidebar && "md:mx-auto md:w-12 md:justify-center md:px-0",
            )}
          >
            <Icon
              icon={faUserChef as IconDefinition}
              size="lg"
              className="shrink-0 text-dines-green"
            />
            <span
              className={cn(
                "ml-3 flex-1 truncate whitespace-nowrap text-left text-white",
                collapseSidebar && "md:hidden",
              )}
            >
              {venues.find((venue) => venue.id === selectedVenue)?.name}
            </span>
            <Icon
              icon={faChevronRight}
              size="sm"
              className={cn(
                "ml-2 shrink-0 text-gray-400",
                // FontAwesome's .svg-inline--fa sets display:inline-block at
                // equal specificity, so plain md:hidden loses; force it.
                collapseSidebar && "md:!hidden",
              )}
            />
          </button>
        </div>
      )}

      <div className="relative min-h-0 flex-1">
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute top-0 right-0 left-0 z-20 h-12 bg-gradient-to-b from-black/15 to-transparent transition-opacity duration-150",
            showTopFade ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute right-0 bottom-0 left-0 z-20 h-12 bg-gradient-to-t from-black/15 to-transparent transition-opacity duration-150",
            showBottomFade ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="no-scrollbar h-full overflow-y-auto overflow-x-hidden overscroll-contain"
        >
          <MainSideBarLinks
            links={links}
            selected={selected}
            collapseSidebar={collapseSidebar}
          />
        </div>
      </div>

      <SideBarProfile
        organisation={organisation}
        collapseSidebar={collapseSidebar}
      />
    </div>
  );
};

export default MainSideBar;
